import React, { FC, useState } from "react";
import { Link } from "react-router-dom";
import NcImage from "shared/NcImage/NcImage";
import api from '../../api';

export interface CarrouselCardProps {
  className?: string;
  data: {
    id: string;
    name: string;
    shortname: string;
    company_type: 'agencies' | 'brandowners';
    agent_rep_regions?: Array<{id: string; name: string; country: string}>;
    images?: Array<{id: string; url: string; caption?: string; key: string}>;
    avatar?: {id: string; url: string; caption?: string};
    brandowner?: {
      name: string;
      avatar?: {id: string; url: string; caption?: string};
    };
  };
  size?: "default" | "small";
}

const CarrouselCard: FC<CarrouselCardProps> = ({
  className = "",
  data,
  size = "default",
}) => {
  const {
    id,
    name,
    shortname,
    company_type,
    agent_rep_regions,
    images,
    avatar,
    brandowner,
  } = data;

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageHovered, setIsImageHovered] = useState(false);
  const [showRegionsTooltip, setShowRegionsTooltip] = useState(false);

  const getAllImages = () => {
    const allImages = [];
    const baseUrl = api.getUploadUrl();

    if (images && images.length > 0) {
      images.forEach(img => {
        if (img.url) {
          const relativeUrl = img.url;
          let imageUrl;
          if (relativeUrl.startsWith('http')) {
            imageUrl = relativeUrl;
          } else {
            const fullPath = relativeUrl.startsWith('uploads/') 
              ? `storage/${relativeUrl}` 
              : relativeUrl;
            imageUrl = `${baseUrl}/${relativeUrl}`;
          }
          allImages.push({
            ...img,
            url: imageUrl,
            type: 'main'
          });
        }
      });
    }
    
    if (allImages.length === 0 && avatar?.url) {
      const relativeUrl = avatar.url;
      let imageUrl;
      if (relativeUrl.startsWith('http')) {
        imageUrl = relativeUrl;
      } else {
        const fullPath = relativeUrl.startsWith('uploads/') 
          ? `storage/${relativeUrl}` 
          : relativeUrl;
        imageUrl = `${baseUrl}/${relativeUrl}`;
      }
      allImages.push({
        ...avatar,
        url: imageUrl,
        type: 'avatar'
      });
    }
    
    if (allImages.length === 0 && brandowner?.avatar?.url) {
      const relativeUrl = brandowner.avatar.url;
      let imageUrl;
      if (relativeUrl.startsWith('http')) {
        imageUrl = relativeUrl;
      } else {
        const fullPath = relativeUrl.startsWith('uploads/') 
          ? `storage/${relativeUrl}` 
          : relativeUrl;
        imageUrl = `${baseUrl}/${relativeUrl}`;
      }
      allImages.push({
        ...brandowner.avatar,
        url: imageUrl,
        type: 'brandowner_avatar'
      });
    }
    
    if (allImages.length === 0) {
      allImages.push({
        id: 'placeholder',
        url: 'https://via.placeholder.com/300x200',
        caption: 'No image available',
        type: 'placeholder'
      });
    }
    
    return allImages;
  };

  const availableImages = getAllImages();
  const hasMultipleImages = availableImages.length > 1;

  const nextImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev + 1) % availableImages.length);
  };

  const prevImage = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev - 1 + availableImages.length) % availableImages.length);
  };

  const goToImage = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentImageIndex(index);
  };

  const renderSliderGallery = () => {
    const currentImage = availableImages[currentImageIndex];
    
    return (
      <div 
        className="relative w-full flex-shrink-0 overflow-hidden rounded-2xl"
        onMouseEnter={() => setIsImageHovered(true)}
        onMouseLeave={() => setIsImageHovered(false)}
      >
        <NcImage
          containerClassName="aspect-w-4 aspect-h-3 sm:aspect-w-6 sm:aspect-h-5"
          className="object-cover w-full h-full transition-transform duration-300"
          src={currentImage.url}
          alt={currentImage.caption || name}
        />
        
        {hasMultipleImages && isImageHovered && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 opacity-90 hover:opacity-100"
              aria-label="Previous image"
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <button
              onClick={nextImage}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 opacity-90 hover:opacity-100"
              aria-label="Next image"
            >
              <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10">
              <div className="flex items-center space-x-2">
                {availableImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={(e) => goToImage(index, e)}
                    className={`image-indicator-dot flex-shrink-0 transition-all duration-200 ${
                      index === currentImageIndex 
                        ? 'bg-white shadow-lg scale-125' 
                        : 'bg-white/50 hover:bg-white/70'
                    }`}
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      border: 'none',
                      padding: '0'
                    }}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderRepresentationRegions = () => {
    if (!agent_rep_regions || agent_rep_regions.length === 0) return null;

    const firstRegion = agent_rep_regions[0];
    const remainingCount = agent_rep_regions.length - 1;

    return (
      <div className="relative">
        <div className="text-xs text-neutral-500 mb-2">Representation Regions:</div>
        <div className="flex items-center gap-1 relative">
          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full whitespace-nowrap">
            {firstRegion.name}
          </span>
          {remainingCount > 0 && (
            <span 
              className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full whitespace-nowrap cursor-pointer relative"
              onMouseEnter={() => setShowRegionsTooltip(true)}
              onMouseLeave={() => setShowRegionsTooltip(false)}
            >
              + {remainingCount} more
              {showRegionsTooltip && (
                <div className="absolute bottom-full right-0 mb-2 z-50 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 w-max max-w-sm">
                  <div className="space-y-1">
                    {agent_rep_regions.slice(1).map((region) => (
                      <div key={region.id} className="whitespace-nowrap text-left">
                        {region.name}
                      </div>
                    ))}
                  </div>
                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-900"></div>
                </div>
              )}
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className={`${size === "default" ? "p-4 space-y-3" : "p-3 space-y-2"}`}>
        <div>
          <h2 className={`font-medium text-neutral-900 ${size === "default" ? "text-lg" : "text-base"}`}>
            <span className="line-clamp-1">{name}</span>
          </h2>
        </div>
        
        {renderRepresentationRegions()}
      </div>
    );
  };

  const linkTo = company_type === 'agencies' 
    ? `/agency/${shortname}` 
    : `/brand/${shortname}`;

  return (
    <div className={`nc-CarouselCard group relative bg-white border border-neutral-200 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow ${className}`}>
      <Link to={linkTo}>
        {renderSliderGallery()}
        {renderContent()}
      </Link>
    </div>
  );
};

export default CarrouselCard;